import { positionAtTime } from "../layers/bullets.js";
import { stepAlongPath } from "../layers/alongPath.js";
import { geoToWorld } from "./projection.js";

export interface MotionFeature {
  id: string;
  path: readonly [number, number][];
  timestamps: readonly number[];
}
export interface MotionSample {
  id: string;
  position: [number, number];
  distanceMeters: number;
}
export interface TycoonMotion {
  step(
    feature: MotionFeature,
    nowSec: number,
    elapsedSec: number,
  ): MotionSample;
  retain(activeIds: ReadonlySet<string>): void;
}
export const isDrawableMotionFeature = (feature: MotionFeature): boolean =>
  feature.path.length >= 2 && feature.path.length === feature.timestamps.length &&
  feature.path.every((point) => point.length === 2 && point.every(Number.isFinite)) &&
  feature.timestamps.every((time, index) =>
    Number.isFinite(time) && (index === 0 || time >= feature.timestamps[index - 1]!)
  );
const cumulative = (path: readonly [number, number][]): number[] => {
  const result = path.length === 0 ? [] : [0];
  for (let i = 1; i < path.length; i += 1) {
    const a = geoToWorld(path[i - 1]!), b = geoToWorld(path[i]!);
    result.push(result.at(-1)! + Math.hypot(b[0] - a[0], b[1] - a[1]));
  }
  return result;
};
const pointAt = (
  path: readonly [number, number][],
  cum: readonly number[],
  distance: number,
): [number, number] => {
  if (path.length === 0) return [0, 0];
  const d = Math.max(0, Math.min(cum.at(-1) ?? 0, distance));
  let i = 1;
  while (i < cum.length - 1 && cum[i]! < d) i += 1;
  if (i >= path.length) return path.at(-1)!;
  const a = path[i - 1]!,
    b = path[i]!,
    span = cum[i]! - cum[i - 1]!,
    f = span === 0 ? 0 : (d - cum[i - 1]!) / span;
  return [a[0] + (b[0] - a[0]) * f, a[1] + (b[1] - a[1]) * f];
};
const project = (
  path: readonly [number, number][],
  cum: readonly number[],
  point: [number, number],
): number => {
  const p = geoToWorld(point);
  let bestError = Infinity, bestDistance = 0;
  for (let i = 1; i < path.length; i += 1) {
    const a = geoToWorld(path[i - 1]!),
      b = geoToWorld(path[i]!),
      dx = b[0] - a[0],
      dy = b[1] - a[1],
      l2 = dx * dx + dy * dy;
    const t = l2 === 0 ? 0 : Math.max(
      0,
      Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / l2),
    );
    const error = Math.hypot(p[0] - a[0] - dx * t, p[1] - a[1] - dy * t);
    if (error < bestError) {
      bestError = error;
      bestDistance = cum[i - 1]! + (cum[i]! - cum[i - 1]!) * t;
    }
  }
  return bestDistance;
};
const validate = (feature: MotionFeature): void => {
  if (feature.path.length !== feature.timestamps.length) {
    throw new Error("path and timestamps must align");
  }
  if (feature.path.length < 2) {
    throw new Error("motion path must contain at least two points");
  }
  if (feature.path.some((p) => p.length !== 2 || !p.every(Number.isFinite))) {
    throw new Error("path coordinates must be finite");
  }
  if (
    feature.timestamps.some((t, i) =>
      !Number.isFinite(t) || (i > 0 && t < feature.timestamps[i - 1]!)
    )
  ) throw new Error("timestamps must be finite and increasing");
};
export function createTycoonMotion(
  options: { catchupMps?: number } = {},
): TycoonMotion {
  const displayed = new Map<
      string,
      { distance: number; path: readonly [number, number][]; cumulative: number[]; position: [number, number] }
    >(),
    catchupMps = options.catchupMps ?? 20;
  return {
    step(feature, nowSec, elapsedSec) {
      validate(feature);
      const prior = displayed.get(feature.id),
        cum = prior?.path === feature.path
          ? prior.cumulative
          : cumulative(feature.path),
        targetPosition = positionAtTime([...feature.path], [
          ...feature.timestamps,
        ], nowSec),
        target = project(feature.path, cum, targetPosition);
      const priorDistance = prior?.path === feature.path
          ? prior.distance
          : prior
          ? project(feature.path, cum, prior.position)
          : undefined;
      const distanceMeters = stepAlongPath(
        priorDistance,
        target,
        catchupMps * Math.max(0, Math.min(0.1, elapsedSec)),
      );
      const position = pointAt(feature.path, cum, distanceMeters);
      displayed.set(feature.id, {
        distance: distanceMeters,
        path: feature.path,
        cumulative: cum,
        position,
      });
      return { id: feature.id, position, distanceMeters };
    },
    retain(activeIds) {
      for (const id of displayed.keys()) {
        if (!activeIds.has(id)) displayed.delete(id);
      }
    },
  };
}
